import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import {
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import {
  getTokens,
  getNavTokens,
  applyCssVars,
  shadeBrand,
  brandAlpha,
  INK,
  SEMANTIC,
  TOPBAR,
} from "./styles/tokens";

import "./styles/animations.css";
import "./styles/premium.css";

const queryClient = new QueryClient();

/* =====================================================================
 * Tema kpers (F1) — construído a partir de getTokens(mode, brand).
 * Os mesmos valores alimentam o tema v4 (@material-ui/core) e o tema v5
 * (@mui/material). A cor da marca vem SEMPRE de colorMode.primaryColor*
 * → theme.palette.primary.main; nenhum hex de marca é fixo aqui.
 * ===================================================================== */

const MOTION = "150ms ease";
const TRANSITION_SURFACE = `background-color ${MOTION}, box-shadow ${MOTION}, border-color ${MOTION}, color ${MOTION}`;

// Seletor de estado nas duas sintaxes (v4 = "$classe", v5 = ".Mui-classe")
const SEL = {
  v4: {
    selected: "&$selected",
    focused: "&$focused",
    disabled: "&$disabled",
    checked: "&$checked",
    shrink: "&$shrink",
    invisible: "&:not($invisible)",
    notched: "& $notchedOutline",
    focusedNotched: "&$focused $notchedOutline",
    hoverNotched: "&:hover $notchedOutline",
    checkedTrack: "&$checked + $track",
    marginDense: "&$marginDense",
    multiline: "&$multiline",
  },
  v5: {
    selected: "&.Mui-selected",
    focused: "&.Mui-focused",
    disabled: "&.Mui-disabled",
    checked: "&.Mui-checked",
    shrink: "&.MuiInputLabel-shrink",
    invisible: "&:not(.MuiBackdrop-invisible)",
    notched: "& .MuiOutlinedInput-notchedOutline",
    focusedNotched: "&.Mui-focused .MuiOutlinedInput-notchedOutline",
    hoverNotched: "&:hover .MuiOutlinedInput-notchedOutline",
    checkedTrack: "&.Mui-checked + .MuiSwitch-track",
    marginDense: "&.MuiInputLabel-sizeSmall",
    multiline: "&.MuiInputBase-multiline",
  },
};

const semanticColor = (hex) => ({
  main: hex,
  light: brandAlpha(hex, 0.1), // tinta
  dark: shadeBrand(hex, -0.2),
  contrastText: "#ffffff",
});

const buildPalette = (t) => {
  const isDark = t.mode === "dark";
  const brand = {
    main: t.brand,
    light: shadeBrand(t.brand, 0.35),
    dark: shadeBrand(t.brand, -0.2),
    contrastText: t.brandOn,
  };
  return {
    primary: brand,
    secondary: { ...brand },
    success: semanticColor(SEMANTIC.success),
    warning: semanticColor(SEMANTIC.warning),
    error: semanticColor(SEMANTIC.error),
    info: semanticColor(SEMANTIC.info),
    background: {
      default: t.page,
      paper: t.paper,
    },
    text: {
      primary: t.text,
      secondary: t.textSecondary,
      disabled: t.textMuted,
      hint: t.textMuted,
    },
    divider: t.line,
    action: {
      hover: isDark ? "rgba(229,233,242,0.06)" : "rgba(18,26,50,0.04)",
      selected: t.tint.soft,
      focus: t.tint.medium,
    },
    // ---- chaves legadas usadas pelas telas (mantidas, agora em tokens) ----
    textPrimary: t.brand,
    borderPrimary: t.brand,
    fontColor: t.brand,
    dark: { main: t.text },
    light: { main: t.muted },
    tabHeaderBackground: t.muted,
    optionsBackground: t.page,
    fancyBackground: t.page,
    total: t.paper,
    messageIcons: t.textSecondary,
    inputBackground: t.paper,
    barraSuperior: t.paper, // shell neutro — a marca entra só como tinta
  };
};

const buildTypography = (t) => ({
  fontFamily: t.font,
  fontSize: 14,
  htmlFontSize: 16,
  h1: { fontSize: 30, fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.02em" },
  h2: { fontSize: 24, fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.02em" },
  h3: { fontSize: 22, fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.01em" },
  h4: { fontSize: 20, fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.01em" },
  h5: { ...t.type.pageTitle, letterSpacing: "-0.01em" },
  h6: { ...t.type.sectionTitle, lineHeight: 1.4 },
  subtitle1: { fontSize: 14, fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { ...t.type.label, lineHeight: 1.5 },
  body1: { fontSize: 14, lineHeight: 1.5 },
  body2: { fontSize: 13, lineHeight: 1.5 },
  caption: { fontSize: 12, lineHeight: 1.4 },
  overline: { ...t.type.groupTitle, lineHeight: 1.6 },
  button: {
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: 0,
  },
});

// Escala de elevação do MUI substituída: 1 = card, 2–7 = soft, 8+ = lift
const buildShadows = (t) => {
  const s = ["none"];
  for (let i = 1; i <= 24; i += 1) {
    if (i === 1) s.push(t.shadow.card);
    else if (i < 8) s.push(t.shadow.soft);
    else s.push(t.shadow.lift);
  }
  return s;
};

const buildScrollbar = (t) => ({
  "&::-webkit-scrollbar": { width: "10px", height: "10px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: t.mode === "light" ? "#cdd5e3" : "#334155",
    borderRadius: "9999px",
    border: "3px solid transparent",
    backgroundClip: "padding-box",
    "&:hover": {
      backgroundColor: t.mode === "light" ? "#b6c0d4" : "#475569",
      backgroundClip: "padding-box",
    },
  },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
});

/**
 * Overrides de componentes. `v` = "v4" | "v5" só muda a sintaxe dos seletores
 * de estado; os valores são idênticos nos dois temas.
 */
const buildOverrides = (t, v) => {
  const s = SEL[v];
  const isDark = t.mode === "dark";
  const line = `1px solid ${t.line}`;
  const tableText = isDark ? t.text : INK[800];
  const tableHeadText = isDark ? t.textSecondary : INK[500];
  const tooltipBg = isDark ? INK[700] : INK[800];
  const inputPadding = "10px 14px";
  // Botão primário do painel: degradê vertical curto na cor da marca.
  const brandFill = `linear-gradient(180deg, ${t.brand}, ${shadeBrand(t.brand, -0.08)})`;
  const brandFillHover = `linear-gradient(180deg, ${t.brandHover}, ${shadeBrand(t.brandHover, -0.1)})`;
  const brandFilled = {
    background: brandFill,
    backgroundColor: t.brand,
    color: t.brandOn,
    border: "1px solid transparent",
    boxShadow: `0 2px 4px ${brandAlpha(t.brand, 0.14)}`,
    "&:hover": {
      background: brandFillHover,
      backgroundColor: t.brandHover,
      boxShadow: `0 3px 7px ${brandAlpha(t.brand, 0.18)}`,
    },
  };

  const menuPaper = {
    borderRadius: t.radius.sm,
    border: line,
    boxShadow: t.shadow.lift,
    backgroundImage: "none",
    backgroundColor: t.paper,
  };

  const selectedTint = {
    [s.selected]: {
      backgroundColor: t.tint.soft,
      "&:hover": { backgroundColor: t.tint.medium },
    },
  };

  return {
    MuiCssBaseline: {
      "@global": {
        body: {
          backgroundColor: t.page,
          color: t.text,
          fontFamily: t.font,
          WebkitFontSmoothing: "antialiased",
          textRendering: "optimizeLegibility",
        },
      },
    },

    // ---------- Superfícies ----------
    MuiPaper: {
      root: {
        backgroundImage: "none",
        backgroundColor: t.paper,
        color: t.text,
        border: line,
        marginLeft: 0,
        marginRight: 0,
        width: "100%",
      },
      rounded: { borderRadius: t.radius.md },
      outlined: { border: line },
    },
    MuiCard: {
      root: {
        borderRadius: t.radius.md,
        border: line,
        boxShadow: t.shadow.card,
        backgroundImage: "none",
      },
    },
    MuiCardHeader: {
      root: { padding: "16px 20px 8px 20px" },
      title: { ...t.type.sectionTitle, lineHeight: 1.4 },
      subheader: { fontSize: 12, color: t.textSecondary },
    },
    MuiCardContent: {
      root: { padding: 20, "&:last-child": { paddingBottom: 20 } },
    },

    // ---------- Botões ----------
    MuiButton: {
      root: {
        borderRadius: t.radius.sm,
        textTransform: "none",
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: 0,
        lineHeight: 1.4,
        minHeight: 40,
        padding: "8px 16px",
        transition: TRANSITION_SURFACE,
        transform: "none",
        "&:hover": { transform: "none" },
        "&:active": { transform: "none" },
      },
      sizeSmall: { minHeight: 32, padding: "5px 12px", fontSize: 12.5 },
      sizeLarge: { minHeight: 44, padding: "10px 20px", fontSize: 14 },
      contained: {
        backgroundColor: t.paper,
        color: isDark ? t.text : INK[800],
        border: line,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: t.muted,
          borderColor: t.lineStrong,
          boxShadow: "none",
        },
        [s.disabled]: {
          // o degradê do primário é background-image: sem isto ele sobrevive ao disabled
          background: t.muted,
          backgroundColor: t.muted,
          color: t.textMuted,
          border: line,
          boxShadow: "none",
        },
      },
      containedPrimary: brandFilled,
      containedSecondary: brandFilled,
      outlined: {
        border: line,
        color: isDark ? t.text : INK[800],
        backgroundColor: t.paper,
        padding: "7px 15px",
        "&:hover": {
          backgroundColor: t.muted,
          border: `1px solid ${t.lineStrong}`,
        },
        [s.disabled]: { color: t.textMuted, border: line },
      },
      outlinedPrimary: {
        border: line,
        color: isDark ? t.text : INK[800],
        "&:hover": { border: `1px solid ${t.lineStrong}`, backgroundColor: t.muted },
      },
      outlinedSecondary: {
        border: line,
        color: isDark ? t.text : INK[800],
        "&:hover": { border: `1px solid ${t.lineStrong}`, backgroundColor: t.muted },
      },
      text: {
        padding: "8px 12px",
        color: isDark ? t.textSecondary : INK[600],
        "&:hover": { backgroundColor: t.tint.soft },
      },
      textPrimary: {
        color: t.brand,
        "&:hover": { backgroundColor: t.tint.soft },
      },
      textSecondary: {
        color: t.brand,
        "&:hover": { backgroundColor: t.tint.soft },
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: t.radius.sm,
        transition: TRANSITION_SURFACE,
        "&:hover": { backgroundColor: t.tint.soft },
      },
    },

    // ---------- Inputs ----------
    MuiOutlinedInput: {
      root: {
        borderRadius: t.radius.sm,
        backgroundColor: t.paper,
        fontSize: 14,
        transition: TRANSITION_SURFACE,
        [s.notched]: { borderColor: t.inputBorder, transition: `border-color ${MOTION}` },
        [s.hoverNotched]: { borderColor: t.lineStrong },
        [s.focused]: { boxShadow: t.shadow.glow, backgroundColor: t.paper },
        [s.focusedNotched]: { borderColor: t.brand, borderWidth: 1 },
        [s.multiline]: { padding: inputPadding },
      },
      input: { padding: inputPadding, height: "auto", lineHeight: "20px" },
      multiline: { padding: inputPadding },
      notchedOutline: { borderColor: t.inputBorder },
      adornedStart: { paddingLeft: 12 },
      adornedEnd: { paddingRight: 12 },
    },
    MuiTextField: {
      root: {
        "& .MuiOutlinedInput-root": { borderRadius: t.radius.sm },
      },
    },
    MuiInputLabel: {
      root: { fontSize: 14 },
      outlined: {
        transform: "translate(14px, 12px) scale(1)",
        [s.shrink]: { transform: "translate(14px, -6px) scale(0.75)" },
        [s.marginDense]: { transform: "translate(14px, 12px) scale(1)" },
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: 14,
        color: t.textSecondary,
        [s.focused]: { color: t.brand },
      },
    },
    MuiFormHelperText: {
      root: { fontSize: 12, marginTop: 4 },
      contained: { marginLeft: 4, marginRight: 4 },
    },
    MuiSelect: {
      select: { "&:focus": { backgroundColor: "transparent" } },
      outlined: {
        borderRadius: t.radius.sm,
        "&:focus": { borderRadius: t.radius.sm },
      },
      icon: { color: t.textMuted },
    },
    MuiSwitch: {
      switchBase: { transition: TRANSITION_SURFACE },
      colorPrimary: {
        [s.checked]: { color: t.brand },
        [s.checkedTrack]: { backgroundColor: t.brand, opacity: 0.5 },
      },
      colorSecondary: {
        [s.checked]: { color: t.brand },
        [s.checkedTrack]: { backgroundColor: t.brand, opacity: 0.5 },
      },
    },
    MuiCheckbox: {
      root: { transition: `color ${MOTION}` },
    },

    // ---------- Chips / badges / avatar ----------
    MuiChip: {
      root: {
        borderRadius: t.radius.badge,
        fontSize: 12,
        fontWeight: 600,
        height: 24,
        backgroundColor: t.status.neutral.bg,
        color: t.status.neutral.fg,
      },
      label: { paddingLeft: 10, paddingRight: 10 },
      colorPrimary: { backgroundColor: t.tint.soft, color: isDark ? shadeBrand(t.brand, 0.35) : t.brand },
      colorSecondary: { backgroundColor: t.tint.soft, color: isDark ? shadeBrand(t.brand, 0.35) : t.brand },
      outlined: { border: line, backgroundColor: "transparent" },
      deleteIcon: { color: "inherit", opacity: 0.6, "&:hover": { color: "inherit", opacity: 1 } },
    },
    MuiBadge: {
      badge: {
        fontSize: 11,
        fontWeight: 600,
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        padding: "0 5px",
      },
      colorPrimary: { backgroundColor: t.tint.medium, color: isDark ? shadeBrand(t.brand, 0.35) : t.brand },
      colorSecondary: { backgroundColor: t.tint.medium, color: isDark ? shadeBrand(t.brand, 0.35) : t.brand },
    },
    MuiAvatar: {
      root: { fontSize: 14, fontWeight: 600 },
      colorDefault: {
        backgroundColor: t.tint.medium,
        color: isDark ? shadeBrand(t.brand, 0.35) : t.brand,
      },
    },

    // ---------- Tabelas ----------
    MuiTableHead: {
      root: {
        backgroundColor: t.muted,
        "& .MuiTableCell-head": {
          fontSize: 11,
          fontWeight: 600,
          textTransform: "none",
          letterSpacing: "0.025em",
          color: tableHeadText,
          backgroundColor: t.muted,
          borderBottom: line,
          lineHeight: 1.5,
        },
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${t.lineRow}`,
        fontSize: 14,
        color: tableText,
        padding: "11px 16px",
        fontVariantNumeric: "tabular-nums",
      },
      head: {
        fontSize: 11,
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: "0.025em",
        color: tableHeadText,
        backgroundColor: t.muted,
        padding: "12px 16px",
      },
      sizeSmall: { padding: "8px 12px" },
    },
    MuiTableRow: {
      root: {
        transition: `background-color ${MOTION}`,
        "tbody &:hover": { backgroundColor: t.rowHover },
        [s.selected]: { backgroundColor: t.tint.soft },
      },
    },
    MuiTablePagination: {
      root: { fontSize: 12, color: t.textSecondary },
    },

    // ---------- Diálogos ----------
    MuiDialog: {
      paper: {
        borderRadius: t.radius.lg,
        border: line,
        boxShadow: t.shadow.lift,
        backgroundImage: "none",
      },
    },
    // Modal do painel: cabeçalho e rodapé na superfície suave, com filete.
    MuiDialogTitle: {
      root: {
        padding: "18px 22px 14px",
        backgroundColor: t.muted,
        color: t.text,
        borderBottom: line,
        fontSize: 18,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        "& .MuiTypography-root": {
          color: "inherit",
          fontSize: 18,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          lineHeight: 1.35,
        },
        "& .MuiIconButton-root": { color: t.textSecondary },
        // o MUI zera o padding-top do conteúdo logo após o título
        "& + .MuiDialogContent-root": { paddingTop: 20 },
      },
    },
    MuiDialogContent: {
      root: { padding: "20px 24px" },
      // cabeçalho e rodapé já trazem o filete; o do `dividers` duplicaria a linha
      dividers: { padding: "20px 24px", borderTop: "none", borderBottom: "none" },
    },
    MuiDialogActions: {
      root: {
        padding: "14px 22px",
        backgroundColor: t.muted,
        borderTop: line,
        gap: 8,
        "& > :not(:first-child)": { marginLeft: 0 },
      },
    },
    MuiBackdrop: {
      root: { backgroundColor: isDark ? "rgba(3,8,18,0.6)" : "rgba(13,25,53,0.4)" },
      invisible: { backgroundColor: "transparent" },
    },

    // ---------- Navegação ----------
    MuiTabs: {
      root: { minHeight: 44 },
      indicator: {
        height: 2,
        borderRadius: 0,
        backgroundColor: t.brand,
      },
    },
    MuiTab: {
      root: {
        textTransform: "none",
        fontWeight: 600,
        fontSize: 14,
        letterSpacing: 0,
        minHeight: 44,
        borderRadius: `${t.radius.sm}px ${t.radius.sm}px 0 0`,
        color: t.textSecondary,
        transition: `color ${MOTION}, background-color ${MOTION}`,
        "&:hover": { color: t.text },
        [s.selected]: {
          color: t.brand,
          fontWeight: 600,
          backgroundColor: brandAlpha(t.brand, isDark ? 0.1 : 0.05),
        },
      },
      textColorPrimary: {
        color: t.textSecondary,
        [s.selected]: { color: t.brand },
      },
      textColorSecondary: {
        color: t.textSecondary,
        [s.selected]: { color: t.brand },
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: tooltipBg,
        color: "#ffffff",
        borderRadius: t.radius.badge,
        fontSize: 12,
        fontWeight: 500,
        padding: "6px 10px",
        boxShadow: t.shadow.soft,
      },
      arrow: { color: tooltipBg },
    },
    MuiMenu: {
      paper: {
        ...menuPaper,
        width: "auto !important",
        maxWidth: "300px !important",
        minWidth: "180px !important",
      },
      list: { padding: 6 },
    },
    MuiPopover: {
      paper: {
        ...menuPaper,
        width: "auto !important",
        maxWidth: "300px !important",
        minWidth: "auto !important",
      },
    },
    MuiMenuItem: {
      root: {
        fontSize: 14,
        minHeight: 36,
        borderRadius: t.radius.badge,
        transition: `background-color ${MOTION}`,
        "&:hover": { backgroundColor: t.muted },
        ...selectedTint,
      },
    },
    MuiListItem: {
      root: { ...selectedTint },
      button: {
        transition: `background-color ${MOTION}`,
        "&:hover": { backgroundColor: t.muted },
        ...selectedTint,
      },
    },
    MuiListItemButton: {
      root: {
        transition: `background-color ${MOTION}`,
        "&:hover": { backgroundColor: t.muted },
        ...selectedTint,
      },
    },
    MuiDivider: {
      root: { backgroundColor: t.line },
    },

    // ---------- Shell (o visual fica em layout/index.js; aqui só a base plana) ----------
    MuiAppBar: {
      root: {
        boxShadow: "none",
        backgroundImage: "none",
        border: "none",
      },
      colorPrimary: {
        backgroundColor: t.paper,
        color: t.text,
        borderBottom: line,
      },
      colorDefault: {
        backgroundColor: t.paper,
        color: t.text,
        borderBottom: line,
      },
    },
    MuiDrawer: {
      paper: {
        backgroundColor: t.paper,
        backgroundImage: "none",
        border: "none",
        borderRight: line,
        color: t.text,
      },
    },
    MuiToolbar: {
      root: { color: t.text },
    },

    // ---------- Container full-bleed (comportamento legado mantido) ----------
    MuiContainer: {
      root: {
        paddingLeft: "0 !important",
        paddingRight: "0 !important",
        maxWidth: "none !important",
        width: "100% !important",
      },
      maxWidthLg: { maxWidth: "none !important" },
      maxWidthMd: { maxWidth: "none !important" },
      maxWidthSm: { maxWidth: "none !important" },
      maxWidthXl: { maxWidth: "none !important" },
      maxWidthXs: { maxWidth: "none !important" },
    },
  };
};

// v5 usa components.X.styleOverrides em vez de overrides.X
const toV5Components = (overrides) =>
  Object.keys(overrides).reduce((acc, key) => {
    if (key === "MuiCssBaseline") {
      acc[key] = { styleOverrides: overrides[key]["@global"] };
    } else {
      acc[key] = { styleOverrides: overrides[key] };
    }
    return acc;
  }, {});

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    localStorage.getItem("primaryColorDark") ||
    "#0B2239";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicBrand } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
      primaryColorLight,
      primaryColorDark,
    }),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
      primaryColorLight,
      primaryColorDark,
    ]
  );

  // Tokens únicos para os dois temas e para as CSS vars
  const tokens = useMemo(
    () => ({
      ...getTokens(mode, mode === "light" ? primaryColorLight : primaryColorDark),
      // o menu lateral usa as duas cores da empresa, qualquer que seja o modo
      nav: getNavTokens(primaryColorLight, primaryColorDark),
    }),
    [mode, primaryColorLight, primaryColorDark]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          scrollbarStyles: buildScrollbar(tokens),
          scrollbarStylesSoft: buildScrollbar(tokens),
          tokens,

          palette: {
            type: mode,
            ...buildPalette(tokens),
          },
          typography: buildTypography(tokens),
          shape: { borderRadius: tokens.radius.sm },
          shadows: buildShadows(tokens),
          overrides: buildOverrides(tokens, "v4"),
          mixins: {
            toolbar: {
              minHeight: TOPBAR.height,
              "@media (max-width:959.95px)": { minHeight: TOPBAR.compact },
            },
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, tokens]
  );

  // Ponte v5: telas com @mui/material não leem o tema v4 (contextos diferentes).
  // Este tema espelha exatamente os mesmos tokens para manter o sistema visual único.
  const themeV5 = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode,
          ...buildPalette(tokens),
        },
        typography: buildTypography(tokens),
        shape: { borderRadius: tokens.radius.sm },
        shadows: buildShadows(tokens),
        components: toV5Components(buildOverrides(tokens, "v5")),
        tokens,
      }),
    [mode, tokens]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    // Revenda: uma única chamada traz a marca do domínio acessado
    // (plataforma, revendedor ou empresa com marca própria). Chaves ausentes
    // caem nos mesmos fallbacks de antes.
    const publicFile = (file, fallback) =>
      file ? getBackendUrl() + "/public/" + file : fallback;
    getPublicBrand()
      .then((brand) => {
        setPrimaryColorLight(brand.primaryColorLight || "#0B2239");
        setPrimaryColorDark(brand.primaryColorDark || "#18C98C");
        setAppLogoLight(publicFile(brand.appLogoLight, defaultLogoLight));
        setAppLogoDark(publicFile(brand.appLogoDark, defaultLogoDark));
        setAppLogoFavicon(publicFile(brand.appLogoFavicon, defaultLogoFavicon));
        setAppName(brand.appName || "Digite AI");
      })
      .catch((error) => {
        console.log("Error reading public brand", error);
        setAppName((current) => current || "Digite AI");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Publica os tokens como CSS vars (--kp-*) e mantém a var legada --primaryColor
  useEffect(() => {
    applyCssVars(tokens);
    document.documentElement.setAttribute("data-theme", tokens.mode);
  }, [tokens]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <ThemeProviderV5 theme={themeV5}>
            <QueryClientProvider client={queryClient}>
              <ActiveMenuProvider>
                <Routes />
              </ActiveMenuProvider>
            </QueryClientProvider>
          </ThemeProviderV5>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
